import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardList, Loader2, RefreshCw } from 'lucide-react';
import { T, knopfStil, reiterStil } from '../../lib/designTokens';
import {
  AuswahlLeiste, DealsTabelle, FilterZeile, PachtzahlenDialog, PhasenFilter, phasenName,
} from './dealsListe';
import UploadAuftraege from './UploadAuftraege';
import { mergeMissingAnlage2, normalizeDachverpachtungPartyLabels } from '../../lib/contractMerging';
// Vertragsart aus dem Deal, Anzeige-Reparatur, Suche und Warteschlange stehen
// seit dem 23.09.2026 in src/lib - die mobile Oberfläche nutzt sie mit.
import {
  ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY,
  CONTRACTS_AM_URL,
  buildContractStateKey,
  buildLegacyContractStateKey,
  hasStoredContractContent,
  kickManualUploadWorker,
  standNichtLadbar,
} from '../../lib/vertragsStand';
import {
  buildPendingDealSearchText,
  isActiveManualUploadBatch,
  normalizePendingDealForDisplay,
  normalizePendingPhaseForDisplay,
  normalizeSearchValue,
  readStoredManualUploadBatchId,
  resolveContractKeyFromPendingDeal,
  resolveKleinspeicherLeaseContractKey,
} from '../../lib/vertragErstellen';
import {
  KLEINSPEICHER_LEASE_ANNEX_KEY,
  KLEINSPEICHER_LEASE_ANNEX_NUMBER,
  KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ,
  LEASE_NUMBER_INITIAL_VALUES,
  buildLeaseNumberMappingFields,
  getLeaseNumberRequirements,
  getMissingLeaseNumberLabels,
  mergeMappingFieldsPreferFilled,
  normalizeLeaseNumberInput,
} from '../../lib/kleinspeicherLeaseAnnex';

const ACTIVE_BULK_BATCH_STORAGE_KEY = 'enpower:contracts-am:activeBulkBatch:v1';
const BULK_POLL_INTERVAL_MS = 3500;
// Upload-Verlauf (25.10.2026): Der Abruf scheiterte an einer kurz überlasteten Ablage
// („Too many connections“). Dann bleibt der letzte Stand stehen, und die Abfrage des
// laufenden Uploads geht weiter - vorher hörte sie auf, und es sah nach einem
// gescheiterten Upload aus.
const UPLOAD_ABRUF_GESCHEITERT = 'upload-abruf-gescheitert';
const UPLOAD_STAND_SPAETER = 'Der Stand ist gerade nicht abrufbar – wird gleich erneut versucht. Der Upload selbst läuft weiter.';

function istVoruebergehenderAbruffehler(fehler) {
  const status = Number(fehler?.response?.status || 0);
  const detail = String(fehler?.response?.data?.detail || fehler?.message || '');
  return !fehler?.response || status === 429 || status >= 500 || /too many connections/i.test(detail);
}
const BULK_CONTRACT_AUTO_KEY = 'auto';
const BULK_UPLOAD_VARIANT_KEYS = ['ci', 'druckversion'];
const CONTRACTS = [
  {
    key: 'kaufvertrag-kleinspeicher',
    label: 'Kaufvertrag Kleinspeicher / Schwarmspeicher',
    variants: [
      { key: 'ci', label: 'CI', file: 'Kaufvertrag_Kleinspeicher_Schwarmspeicher_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Kaufvertrag_Kleinspeicher_Schwarmspeicher_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'projektrechte-pv',
    label: 'Projektrechte PV Kaufvertrag',
    variants: [
      { key: 'ci', label: 'CI', file: 'Projektrechte_PV_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Projektrechte_PV_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'dachnutzungsvertrag-sanierung',
    label: 'Dachnutzungsvertrag Sanierung',
    variants: [
      { key: 'ci', label: 'CI', file: 'Dachnutzungsvertrag_Sanierung_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Dachnutzungsvertrag_Sanierung_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-dachverpachtung-ez',
    label: 'Pachtvertrag Dachverpachtung (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Dachverpachtung_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Dachverpachtung_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-dachverpachtung-jz',
    label: 'Pachtvertrag Dachverpachtung (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Dachverpachtung_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Dachverpachtung_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-kleinspeicher-ez',
    label: 'Pachtvertrag Kleinspeicher (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Kleinspeicher_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Kleinspeicher_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-kleinspeicher-jz',
    label: 'Pachtvertrag Kleinspeicher (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Kleinspeicher_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-grossspeicher-ez',
    label: 'Pachtvertrag Großspeicher (Einmalzahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Grossspeicher_EZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Grossspeicher_EZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
  {
    key: 'pachtvertrag-grossspeicher-jz',
    label: 'Pachtvertrag Großspeicher (Jahreszahlung)',
    variants: [
      { key: 'ci', label: 'CI', file: 'Pachtvertrag_Grossspeicher_JZ_ENPOWER_CI.html' },
      { key: 'druckversion', label: 'Druckversion', file: 'Pachtvertrag_Grossspeicher_JZ_ENPOWER_CI_Druckversion.html' },
    ],
  },
];

function readStoredBulkBatchId() {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(ACTIVE_BULK_BATCH_STORAGE_KEY) || '';
  } catch (_error) {
    return '';
  }
}

function persistBulkBatchId(batchId) {
  if (typeof window === 'undefined') return;
  try {
    if (batchId) {
      window.localStorage.setItem(ACTIVE_BULK_BATCH_STORAGE_KEY, batchId);
    } else {
      window.localStorage.removeItem(ACTIVE_BULK_BATCH_STORAGE_KEY);
    }
  } catch (_error) {
    // Nur Komfort für Reload-Resume.
  }
}

function persistManualUploadBatchId(batchId) {
  if (typeof window === 'undefined') return;
  try {
    if (batchId) {
      window.localStorage.setItem(ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY, batchId);
    } else {
      window.localStorage.removeItem(ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY);
    }
  } catch (_error) {
    // Nur Komfort für Reload-Resume.
  }
}

function getBulkLeaseNumberRequirements(items = [], fallbackContractKey = KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ) {
  return getLeaseNumberRequirements(
    (Array.isArray(items) ? items : []).map((item) => resolveKleinspeicherLeaseContractKey(item, fallbackContractKey))
  );
}

function sanitizeDownloadFilename(value) {
  return String(value || 'Vertrag.html')
    .replace(/[<>:"/\\|?*\u0000-\u001F]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || 'Vertrag.html';
}

async function downloadGeneratedFile(url, filename, headers = {}) {
  const safeFilename = sanitizeDownloadFilename(filename);
  try {
    const response = await fetch(url, { credentials: 'omit', headers });
    if (!response.ok) throw new Error(`Download fehlgeschlagen (${response.status}).`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = safeFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
  } catch (_error) {
    const link = document.createElement('a');
    link.href = url;
    link.download = safeFilename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

function applySourceHtmlToTemplate(baseHtml, sourceHtml) {
  if (typeof baseHtml !== 'string' || !baseHtml) return baseHtml;
  if (typeof sourceHtml !== 'string' || !sourceHtml) return baseHtml;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(baseHtml, 'text/html');
    const source = doc.getElementById('contract-source');
    if (!source) return baseHtml;
    source.innerHTML = sourceHtml;
    return '<!doctype html>' + doc.documentElement.outerHTML;
  } catch (error) {
    console.error('applySourceHtmlToTemplate failed:', error);
    return baseHtml;
  }
}

async function loadContractTemplateHtml({ apiBase, authHeaders, isAuthenticated, contractKey, variantKey }) {
  const contract = CONTRACTS.find((candidate) => candidate.key === contractKey);
  if (!contract) {
    throw new Error('Vertragsart konnte nicht automatisch zugeordnet werden.');
  }

  const variant = contract.variants.find((candidate) => candidate.key === variantKey) || contract.variants[0];
  const stateKey = buildContractStateKey(contract.key);
  const legacyStateKey = buildLegacyContractStateKey(contract.key, variant.key);
  const originalUrl = `/contracts/${variant.file}`;
  const originalPromise = fetch(originalUrl).then((response) => (response.ok ? response.text() : ''));
  let html = null;

  if (isAuthenticated) {
    try {
      const stateResp = await axios.get(`${apiBase}/contracts/state`, {
        params: { contract_key: stateKey },
        headers: authHeaders,
      });
      let state = stateResp.data?.state;
      let stateSource = 'canonical';
      if (!hasStoredContractContent(state) && legacyStateKey !== stateKey) {
        try {
          const legacyResp = await axios.get(`${apiBase}/contracts/state`, {
            params: { contract_key: legacyStateKey },
            headers: authHeaders,
          });
          const legacyState = legacyResp.data?.state || null;
          if (hasStoredContractContent(legacyState)) {
            state = legacyState;
            stateSource = 'legacy';
          }
        } catch (_legacyError) {
          // Legacy fallback ist nur lesend und optional.
        }
      }
      const sourceHtml = typeof state?.editorState?.sourceHtml === 'string' ? state.editorState.sourceHtml : '';
      if (sourceHtml && stateSource === 'canonical') {
        const baseHtml = await originalPromise;
        if (baseHtml) {
          html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
        }
      } else if (state?.content) {
        html = state.content;
      } else if (sourceHtml) {
        const baseHtml = await originalPromise;
        if (baseHtml) {
          html = applySourceHtmlToTemplate(baseHtml, sourceHtml);
        }
      }
    } catch (error) {
      // Bis 09.10.2026 hier still die Originaldatei: Wer das Vorlagen-Recht nicht hatte,
      // erstellte per Bulk auf einer alten Fassung. Jetzt bekommt die Zeile den Grund.
      throw standNichtLadbar(error);
    }
  }

  const originalHtml = await originalPromise;
  if (!html) {
    if (!originalHtml) throw new Error(`Vorlage "${variant.file}" wurde nicht gefunden.`);
    html = originalHtml;
  } else if (originalHtml) {
    html = mergeMissingAnlage2(html, originalHtml);
  }

  html = normalizeDachverpachtungPartyLabels(html, contract.key);
  return { contract, variant, stateKey, html };
}

async function loadContractMappingFields({ authHeaders, isAuthenticated, contractKey }) {
  if (!isAuthenticated || !contractKey) return {};
  try {
    const response = await axios.get(CONTRACTS_AM_URL, {
      params: { op: 'mapping', contract_key: buildContractStateKey(contractKey) },
      headers: authHeaders,
    });
    const fields = response.data?.mapping?.fields;
    if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return {};
    return fields;
  } catch (error) {
    console.warn('Bulk-Mapping konnte nicht geladen werden:', contractKey, error);
    return {};
  }
}

export default function VertragsAmPendingBoard({ apiBase = '/api', session = null, onSelectDeal = null }) {
  const accessToken = session?.access_token || null;
  const isAuthenticated = Boolean(accessToken);

  const [items, setItems] = useState([]);
  const [phases, setPhases] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activePhaseId, setActivePhaseId] = useState('all');
  const [responsibleFilter, setResponsibleFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [queueSearchQuery, setQueueSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedDealIds, setSelectedDealIds] = useState([]);
  const [bulkContractKey, setBulkContractKey] = useState(BULK_CONTRACT_AUTO_KEY);
  const [bulkAppendLeaseContract, setBulkAppendLeaseContract] = useState(false);
  const [bulkLeaseContractKey, setBulkLeaseContractKey] = useState(KLEINSPEICHER_LEASE_CONTRACT_KEY_JZ);
  const [bulkLeaseNumbers, setBulkLeaseNumbers] = useState(LEASE_NUMBER_INITIAL_VALUES);
  const [bulkLeaseNumbersModalOpen, setBulkLeaseNumbersModalOpen] = useState(false);
  const [bulkLeaseNumbersConfirmed, setBulkLeaseNumbersConfirmed] = useState(false);
  const [bulkAppendDatasheet, setBulkAppendDatasheet] = useState(false);
  const [bulkDownloadMode, setBulkDownloadMode] = useState('none');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkStatusByDealId, setBulkStatusByDealId] = useState({});
  const [bulkSummary, setBulkSummary] = useState(null);
  const [bulkBatchId, setBulkBatchId] = useState(readStoredBulkBatchId);
  const [queueTab, setQueueTab] = useState('deals');
  const [manualUploadBatchId, setManualUploadBatchId] = useState(readStoredManualUploadBatchId);
  const [manualUploadBatch, setManualUploadBatch] = useState(null);
  const [manualUploadHistory, setManualUploadHistory] = useState([]);
  const [manualUploadLoading, setManualUploadLoading] = useState(false);
  const [manualUploadError, setManualUploadError] = useState('');
  const [manualUploadHinweis, setManualUploadHinweis] = useState('');
  const downloadedBulkBatchIdsRef = useRef(new Set());
  const notifiedManualUploadBatchIdsRef = useRef(new Set());

  const authHeaders = useMemo(() => (
    accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
  ), [accessToken]);

  const loadPendingDeals = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      setPhases([]);
      setFetchedAt(null);
      setError('Bitte zuerst einloggen.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiBase}/contracts/usage`, {
        params: { op: 'pending', _ts: Date.now() },
        headers: authHeaders,
      });
      setItems(Array.isArray(response.data?.items) ? response.data.items.map(normalizePendingDealForDisplay) : []);
      setPhases(Array.isArray(response.data?.phases) ? response.data.phases.map(normalizePendingPhaseForDisplay) : []);
      setFetchedAt(response.data?.fetchedAt || new Date().toISOString());
    } catch (loadError) {
      console.error(loadError);
      setItems([]);
      setPhases([]);
      setFetchedAt(null);
      setError(loadError?.response?.data?.detail || loadError?.message || 'Bitrix-Liste konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated]);

  useEffect(() => {
    if (queueTab === 'deals') {
      loadPendingDeals();
    }
  }, [loadPendingDeals, queueTab]);

  const loadManualUploadQueue = useCallback(async ({ drain = true, batchId = manualUploadBatchId, quiet = false } = {}) => {
    if (!isAuthenticated) {
      setManualUploadBatch(null);
      setManualUploadHistory([]);
      setManualUploadError('Bitte zuerst einloggen.');
      return null;
    }

    if (!quiet) setManualUploadLoading(true);
    setManualUploadError('');
    try {
      const params = { op: 'am-upload-queue', drain: drain ? '1' : '0' };
      const activeBatchId = String(batchId || '').trim();
      if (activeBatchId) params.batch_id = activeBatchId;
      const response = await axios.get(`${apiBase}/contracts/usage`, {
        params,
        headers: authHeaders,
      });
      const batch = response.data?.batch || null;
      const activeBatch = isActiveManualUploadBatch(batch) ? batch : null;
      setManualUploadBatch(activeBatch);
      // Ohne Verlauf (historyFehler) bleibt der zuletzt geladene stehen.
      if (response.data?.historyFehler) {
        setManualUploadHinweis('Der Verlauf ist gerade nicht abrufbar – unten steht der zuletzt geladene Stand.');
      } else {
        setManualUploadHinweis('');
        setManualUploadHistory(Array.isArray(response.data?.history) ? response.data.history : []);
      }
      if (activeBatch?.id && activeBatch.id !== manualUploadBatchId) {
        setManualUploadBatchId(activeBatch.id);
        persistManualUploadBatchId(activeBatch.id);
      } else if (batch && !activeBatch) {
        setManualUploadBatchId('');
        persistManualUploadBatchId('');
      }
      if (drain && activeBatch) {
        kickManualUploadWorker(apiBase, authHeaders);
      }
      return batch;
    } catch (loadError) {
      console.error(loadError);
      if (istVoruebergehenderAbruffehler(loadError)) {
        setManualUploadHinweis(UPLOAD_STAND_SPAETER);
      } else {
        setManualUploadError(loadError?.response?.data?.detail || loadError?.message || 'Upload-Warteschlange konnte nicht geladen werden.');
      }
      return UPLOAD_ABRUF_GESCHEITERT;
    } finally {
      if (!quiet) setManualUploadLoading(false);
    }
  }, [apiBase, authHeaders, isAuthenticated, manualUploadBatchId]);

  useEffect(() => {
    loadManualUploadQueue({ drain: false, quiet: true });
  }, [loadManualUploadQueue]);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') return undefined;

    const handleQueued = (event) => {
      const batchId = String(event?.detail?.batchId || readStoredManualUploadBatchId() || '').trim();
      if (!batchId) return;
      setManualUploadBatchId(batchId);
      persistManualUploadBatchId(batchId);
      loadManualUploadQueue({ drain: true, batchId, quiet: true });
    };

    const handleStorage = (event) => {
      if (event.key !== ACTIVE_MANUAL_UPLOAD_BATCH_STORAGE_KEY) return;
      const batchId = String(event.newValue || '').trim();
      setManualUploadBatchId(batchId);
      if (batchId) {
        loadManualUploadQueue({ drain: true, batchId, quiet: true });
      } else {
        setManualUploadBatch(null);
      }
    };

    window.addEventListener('enpower-contract-manual-upload-queued', handleQueued);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('enpower-contract-manual-upload-queued', handleQueued);
      window.removeEventListener('storage', handleStorage);
    };
  }, [isAuthenticated, loadManualUploadQueue]);

  useEffect(() => {
    if (!manualUploadBatchId || !isAuthenticated || typeof window === 'undefined') return undefined;

    let cancelled = false;
    let timer = null;

    const runPoll = async () => {
      const batch = await loadManualUploadQueue({ drain: true, batchId: manualUploadBatchId, quiet: true });
      if (cancelled) return;
      if (batch === UPLOAD_ABRUF_GESCHEITERT) {
        timer = window.setTimeout(runPoll, BULK_POLL_INTERVAL_MS * 2);
        return;
      }
      if (!batch) return;

      if (batch.done) {
        if (!notifiedManualUploadBatchIdsRef.current.has(batch.id)) {
          notifiedManualUploadBatchIdsRef.current.add(batch.id);
          if (Number(batch.failed || 0) > 0) {
            toast.error(`Upload-Auftrag abgeschlossen: ${batch.completed || 0} erfolgreich, ${batch.failed || 0} fehlgeschlagen.`);
          } else {
            toast.success('Upload-Auftrag erfolgreich abgeschlossen.');
          }
        }
        loadPendingDeals();
        return;
      }

      timer = window.setTimeout(runPoll, BULK_POLL_INTERVAL_MS);
    };

    runPoll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isAuthenticated, loadManualUploadQueue, loadPendingDeals, manualUploadBatchId]);

  useEffect(() => {
    setSelectedDealIds((current) => current.filter((dealId) => items.some((item) => String(item?.dealId || '') === dealId)));
  }, [items]);

  const phaseFilterOptions = useMemo(() => {
    const options = [{ phaseId: 'all', phaseLabel: 'Alle', count: items.length }];
    return options.concat(phases.map((phase) => ({
      ...phase,
      phaseLabel: phasenName(phase.phaseId, phase.phaseLabel),
    })));
  }, [items.length, phases]);

  const responsibleOptions = useMemo(() => {
    const values = Array.from(new Set(
      items
        .map((item) => String(item?.responsible || '').trim())
        .filter(Boolean)
    ));
    return values.sort((a, b) => a.localeCompare(b, 'de'));
  }, [items]);

  const typeOptions = useMemo(() => {
    const values = Array.from(new Set(
      items
        .map((item) => String(item?.typeLabel || '').trim())
        .filter(Boolean)
    ));
    return values.sort((a, b) => a.localeCompare(b, 'de'));
  }, [items]);

  const filteredItems = useMemo(() => {
    const searchTerm = normalizeSearchValue(queueSearchQuery);
    const filtered = items.filter((item) => {
      if (activePhaseId !== 'all' && item.phaseId !== activePhaseId) return false;
      if (responsibleFilter !== 'all' && String(item?.responsible || '') !== responsibleFilter) return false;
      if (typeFilter !== 'all' && String(item?.typeLabel || '') !== typeFilter) return false;
      if (searchTerm) {
        const assignedContractKey = bulkContractKey === BULK_CONTRACT_AUTO_KEY
          ? resolveContractKeyFromPendingDeal(item)
          : bulkContractKey;
        const assignedContractLabel = CONTRACTS.find((contract) => contract.key === assignedContractKey)?.label || assignedContractKey;
        const dealId = String(item?.dealId || '').trim();
        const bulkStatus = bulkStatusByDealId[dealId] || null;
        const statusText = bulkStatus ? `${bulkStatus.state || ''} ${bulkStatus.message || ''}` : '';
        const haystack = buildPendingDealSearchText(item, `${assignedContractLabel} ${statusText}`);
        const searchParts = searchTerm.split(/\s+/).filter(Boolean);
        if (!searchParts.every((part) => haystack.includes(part))) return false;
      }
      return true;
    });

    filtered.sort((a, b) => {
      const left = String(a?.dealTitle || '');
      const right = String(b?.dealTitle || '');
      const result = left.localeCompare(right, 'de');
      return sortOrder === 'asc' ? result : -result;
    });

    return filtered;
  }, [activePhaseId, bulkContractKey, bulkStatusByDealId, items, queueSearchQuery, responsibleFilter, sortOrder, typeFilter]);

  const selectedDealIdSet = useMemo(() => new Set(selectedDealIds), [selectedDealIds]);
  const resolveBulkContractKey = useCallback((item) => (
    bulkContractKey === BULK_CONTRACT_AUTO_KEY
      ? resolveContractKeyFromPendingDeal(item)
      : bulkContractKey
  ), [bulkContractKey]);
  const bulkCapableFilteredItems = useMemo(
    () => filteredItems.filter((item) => resolveBulkContractKey(item)),
    [filteredItems, resolveBulkContractKey]
  );
  const selectedItems = useMemo(
    () => filteredItems.filter((item) => selectedDealIdSet.has(String(item?.dealId || ''))),
    [filteredItems, selectedDealIdSet]
  );
  const bulkLeaseNumberRequirements = useMemo(
    () => getBulkLeaseNumberRequirements(selectedItems, bulkLeaseContractKey),
    [bulkLeaseContractKey, selectedItems]
  );
  const allBulkCapableVisibleSelected = bulkCapableFilteredItems.length > 0
    && bulkCapableFilteredItems.every((item) => selectedDealIdSet.has(String(item?.dealId || '')));

  const updateBulkLeaseNumber = useCallback((field, value) => {
    setBulkLeaseNumbers((current) => ({
      ...current,
      [field]: value,
    }));
    setBulkLeaseNumbersConfirmed(false);
  }, []);

  const toggleDealSelection = useCallback((item) => {
    const dealId = String(item?.dealId || '').trim();
    if (!dealId || !resolveBulkContractKey(item) || bulkRunning) return;
    setSelectedDealIds((current) => (
      current.includes(dealId)
        ? current.filter((candidate) => candidate !== dealId)
        : [...current, dealId]
    ));
  }, [bulkRunning, resolveBulkContractKey]);

  const toggleVisibleSelection = useCallback(() => {
    if (bulkRunning) return;
    const visibleIds = bulkCapableFilteredItems.map((item) => String(item?.dealId || '')).filter(Boolean);
    if (visibleIds.length === 0) return;
    setSelectedDealIds((current) => {
      const currentSet = new Set(current);
      const allSelected = visibleIds.every((dealId) => currentSet.has(dealId));
      if (allSelected) {
        return current.filter((dealId) => !visibleIds.includes(dealId));
      }
      visibleIds.forEach((dealId) => currentSet.add(dealId));
      return Array.from(currentSet);
    });
  }, [bulkCapableFilteredItems, bulkRunning]);

  const applyBulkBatchStatus = useCallback((batch) => {
    if (!batch || !Array.isArray(batch.jobs)) return;
    setBulkSummary({
      total: Number(batch.total || batch.jobs.length || 0),
      completed: Number(batch.completed || 0),
      failed: Number(batch.failed || 0),
      pending: Number(batch.pending || 0),
      running: Number(batch.running || 0),
      percent: Number(batch.percent || 0),
    });
    setBulkStatusByDealId((current) => {
      const next = { ...current };
      batch.jobs.forEach((job) => {
        const dealId = String(job?.dealId || '').trim();
        if (!dealId) return;
        if (job.status === 'completed') {
          next[dealId] = {
            state: 'success',
            message: job.contractNumber ? `Erstellt (${job.contractNumber})` : 'Erstellt und hinterlegt',
            contractNumber: job.contractNumber || '',
            storagePath: job.storagePath || '',
            downloadFiles: Array.isArray(job.downloadFiles) ? job.downloadFiles : [],
          };
        } else if (job.status === 'failed') {
          next[dealId] = {
            state: 'error',
            message: job.message || job.error?.message || 'Erstellung fehlgeschlagen.',
          };
        } else if (job.status === 'running') {
          next[dealId] = { state: 'running', message: 'Wird erstellt...' };
        } else if (job.status === 'pending') {
          next[dealId] = { state: 'running', message: 'Wartet...' };
        }
      });
      return next;
    });
  }, []);

  const downloadBulkBatchFiles = useCallback(async (batch) => {
    const completedFiles = (Array.isArray(batch?.jobs) ? batch.jobs : [])
      .filter((job) => job.status === 'completed')
      .flatMap((job) => {
        const files = Array.isArray(job.downloadFiles) ? job.downloadFiles : [];
        if (files.length) return files;
        return job.downloadUrl ? [{
          format: 'html',
          url: job.downloadUrl,
          filename: job.downloadFilename || `Vertrag - ${job.contractLabel || job.contractKey || 'AM'} - Deal ${job.dealId || ''}.html`,
        }] : [];
      });
    if (!completedFiles.length) {
      toast.warning('Verträge erstellt, aber es kamen keine Download-Links zurück.');
      return;
    }

    if (completedFiles.length > 1 && batch?.id) {
      const formats = Array.from(new Set(completedFiles
        .map((file) => String(file?.format || '').trim().toLowerCase())
        .filter((format) => format === 'html' || format === 'pdf')));
      const params = new URLSearchParams({
        op: 'am-bulk-download-zip',
        batch_id: batch.id,
      });
      if (formats.length) params.set('formats', formats.join(','));
      await downloadGeneratedFile(
        `${apiBase}/contracts/usage?${params.toString()}`,
        `Verträge ${batch.id}.zip`,
        authHeaders,
      );
      toast.success(`${completedFiles.length} Dateien als ZIP-Archiv gestartet.`);
      return;
    }

    for (const file of completedFiles) {
      await downloadGeneratedFile(
        file.url,
        file.filename || `Vertrag.${file.format === 'pdf' ? 'pdf' : 'html'}`,
        authHeaders,
      );
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    toast.success(`${completedFiles.length} Download${completedFiles.length === 1 ? '' : 's'} gestartet.`);
  }, [apiBase, authHeaders]);

  const pollBulkBatch = useCallback(async ({ drain = true } = {}) => {
    if (!bulkBatchId || !isAuthenticated) return null;
    const response = await axios.get(`${apiBase}/contracts/usage`, {
      params: { op: 'am-bulk', batch_id: bulkBatchId, drain: drain ? '1' : '0' },
      headers: authHeaders,
    });
    const batch = response.data?.batch || null;
    if (batch) applyBulkBatchStatus(batch);
    return batch;
  }, [apiBase, applyBulkBatchStatus, authHeaders, bulkBatchId, isAuthenticated]);

  useEffect(() => {
    if (!bulkBatchId || !isAuthenticated) return undefined;

    let cancelled = false;
    let timer = null;
    setBulkRunning(true);

    const runPoll = async () => {
      try {
        const batch = await pollBulkBatch({ drain: true });
        if (cancelled || !batch) return;

        if (batch.done) {
          setBulkRunning(false);
          setBulkBatchId('');
          persistBulkBatchId('');
          const shouldDownload = batch.jobs?.some((job) => job.downloadRequested === true) && !downloadedBulkBatchIdsRef.current.has(batch.id);
          if (shouldDownload) {
            downloadedBulkBatchIdsRef.current.add(batch.id);
            await downloadBulkBatchFiles(batch);
          }
          if (Number(batch.failed || 0) > 0) {
            toast.error(`Erstellung abgeschlossen: ${batch.completed} erfolgreich, ${batch.failed} fehlgeschlagen.`);
          } else {
            toast.success(`${batch.completed} Verträge wurden erstellt und hinterlegt.`);
          }
          loadPendingDeals();
          return;
        }

        timer = window.setTimeout(runPoll, BULK_POLL_INTERVAL_MS);
      } catch (statusError) {
        if (cancelled) return;
        console.error(statusError);
        setBulkRunning(false);
        toast.error(statusError?.response?.data?.detail || statusError?.message || 'Der Stand der Erstellung ließ sich nicht laden.');
      }
    };

    runPoll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [bulkBatchId, downloadBulkBatchFiles, isAuthenticated, loadPendingDeals, pollBulkBatch]);

  const runBulkGeneration = useCallback(async (options = {}) => {
    if (!isAuthenticated) {
      toast.error('Bitte zuerst einloggen.');
      return;
    }
    if (bulkRunning) return;
    if (selectedItems.length === 0) {
      toast.error('Bitte mindestens einen zuordenbaren Deal auswählen.');
      return;
    }

    const bypassLeaseNumberPrompt = options?.bypassLeaseNumberPrompt === true;
    const leaseNumberRequirements = getBulkLeaseNumberRequirements(selectedItems, bulkLeaseContractKey);
    if (bulkAppendLeaseContract) {
      const missingLeaseNumbers = getMissingLeaseNumberLabels(bulkLeaseNumbers, leaseNumberRequirements);
      if (!bypassLeaseNumberPrompt && (!bulkLeaseNumbersConfirmed || missingLeaseNumbers.length > 0)) {
        setBulkLeaseNumbersModalOpen(true);
        if (missingLeaseNumbers.length > 0) {
          toast.error(`Bitte zuerst angeben: ${missingLeaseNumbers.join(', ')}`);
        }
        return;
      }
      if (missingLeaseNumbers.length > 0) {
        setBulkLeaseNumbersModalOpen(true);
        toast.error(`Bitte zuerst angeben: ${missingLeaseNumbers.join(', ')}`);
        return;
      }
    }

    setBulkRunning(true);
    setBulkSummary({ total: selectedItems.length, completed: 0, failed: 0, pending: selectedItems.length, running: 0, percent: 0 });

    const jobs = [];
    const templateCache = new Map();
    const mappingCache = new Map();
    const leaseNumberMappingFields = buildLeaseNumberMappingFields(bulkLeaseNumbers, leaseNumberRequirements);
    const getTemplate = async (contractKey, variantKey) => {
      const cacheKey = `${contractKey}:${variantKey}`;
      if (!templateCache.has(cacheKey)) {
        templateCache.set(cacheKey, loadContractTemplateHtml({
          apiBase,
          authHeaders,
          isAuthenticated,
          contractKey,
          variantKey,
        }));
      }
      return templateCache.get(cacheKey);
    };
    const getMappingFields = async (contractKey) => {
      const cacheKey = String(contractKey || '');
      if (!cacheKey) return {};
      if (!mappingCache.has(cacheKey)) {
        mappingCache.set(cacheKey, loadContractMappingFields({
          authHeaders,
          isAuthenticated,
          contractKey: cacheKey,
        }));
      }
      return mappingCache.get(cacheKey);
    };

    for (const item of selectedItems) {
      const dealId = String(item?.dealId || '').trim();
      const contractKey = resolveBulkContractKey(item);

      setBulkStatusByDealId((current) => ({
        ...current,
        [dealId]: { state: 'running', message: 'Vorlage wird vorbereitet...' },
      }));

      try {
        if (!contractKey) {
          throw new Error('Vertragsart konnte nicht automatisch zugeordnet werden.');
        }

        const templates = await Promise.all(BULK_UPLOAD_VARIANT_KEYS.map((variantKey) => getTemplate(contractKey, variantKey)));
        const template = templates.find((entry) => entry?.variant?.key === 'ci') || templates[0];
        const attachments = [];

        if (bulkAppendLeaseContract) {
          const leaseContractKey = resolveKleinspeicherLeaseContractKey(item, bulkLeaseContractKey);
          const leaseTemplates = await Promise.all(BULK_UPLOAD_VARIANT_KEYS.map((variantKey) => getTemplate(leaseContractKey, variantKey)));
          const leaseTemplate = leaseTemplates.find((entry) => entry?.variant?.key === template?.variant?.key) || leaseTemplates[0];
          const leaseContract = CONTRACTS.find((candidate) => candidate.key === leaseContractKey);
          const [mainMappingFields, leaseMappingFields] = await Promise.all([
            getMappingFields(template.stateKey),
            getMappingFields(leaseTemplate.stateKey),
          ]);
          attachments.push({
            type: 'contract-html',
            key: KLEINSPEICHER_LEASE_ANNEX_KEY,
            contractKey: leaseTemplate.stateKey,
            variantKey: leaseTemplate.variant.key,
            label: leaseContract?.label || 'Pachtvertrag Kleinspeicher',
            anlageNumber: KLEINSPEICHER_LEASE_ANNEX_NUMBER,
            templateHtml: leaseTemplate.html,
            variantTemplates: leaseTemplates.map((entry) => ({
              variantKey: entry.variant.key,
              variantLabel: entry.variant.label,
              templateHtml: entry.html,
            })),
            mapping: {
              fields: mergeMappingFieldsPreferFilled(
                leaseMappingFields,
                mainMappingFields,
                leaseNumberMappingFields
              ),
            },
            literalFields: leaseNumberMappingFields,
          });
        }

        if (bulkAppendDatasheet) {
          attachments.push({
            type: 'static-datasheet',
            key: 'alphaess-storion-lc-tb125-datasheet',
            label: 'Technische Beschreibung/Datenblatt Alpha ESS Storion LC TB 125',
            anlageNumber: 9,
          });
        }

        jobs.push({
          id: `${dealId}-${template.stateKey}-ci-druckversion`,
          dealId,
          dealTitle: item?.dealTitle || '',
          contractKey: template.stateKey,
          contractLabel: template.contract?.label || template.stateKey,
          variantKey: 'ci',
          templateHtml: template.html,
          templateVariants: templates.map((entry) => ({
            variantKey: entry.variant.key,
            variantLabel: entry.variant.label,
            templateHtml: entry.html,
          })),
          attachments,
          downloadRequested: bulkDownloadMode !== 'none',
          downloadFormats: bulkDownloadMode === 'both'
            ? ['html', 'pdf']
            : bulkDownloadMode === 'html' || bulkDownloadMode === 'pdf'
              ? [bulkDownloadMode]
              : [],
          downloadNameHint: item?.dealTitle || '',
        });
        setBulkStatusByDealId((current) => ({
          ...current,
          [dealId]: {
            state: 'running',
            message: 'In Warteschlange...',
          },
        }));
      } catch (error) {
        console.error(error);
        setBulkStatusByDealId((current) => ({
          ...current,
          [dealId]: {
            state: 'error',
            message: error?.response?.data?.detail || error?.message || 'Erstellung fehlgeschlagen.',
          },
        }));
      }
    }

    if (jobs.length === 0) {
      setBulkRunning(false);
      setBulkSummary({ total: selectedItems.length, completed: 0, failed: selectedItems.length, pending: 0, running: 0, percent: 100 });
      toast.error('Die Erstellung konnte nicht starten: Keine Vorlage ließ sich vorbereiten.');
      return;
    }

    try {
      const response = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'am-bulk-enqueue',
        jobs,
      }, { headers: authHeaders });
      const batchId = response.data?.batchId || response.data?.batch?.id || '';
      if (!batchId) throw new Error('Der Server hat keine Auftragsnummer geliefert.');
      setBulkBatchId(batchId);
      persistBulkBatchId(batchId);
      if (response.data?.batch) applyBulkBatchStatus(response.data.batch);
      toast.success(`${jobs.length} ${jobs.length === 1 ? 'Vertrag wird' : 'Verträge werden'} erstellt. Das läuft auch weiter, wenn du die Seite schließt.`);
    } catch (error) {
      console.error(error);
      setBulkRunning(false);
      toast.error(error?.response?.data?.detail || error?.message || 'Die Erstellung konnte nicht starten.');
    }
    return;

    /*
    setBulkRunning(false);
    if (failed > 0) {
      toast.error(`Erstellung abgeschlossen: ${completed} erfolgreich, ${failed} fehlgeschlagen.`);
    } else {
      toast.success(`${completed} Verträge wurden erstellt und hinterlegt.`);
    }
    loadPendingDeals();
    */
  }, [apiBase, applyBulkBatchStatus, authHeaders, bulkAppendDatasheet, bulkAppendLeaseContract, bulkDownloadMode, bulkLeaseContractKey, bulkLeaseNumbers, bulkLeaseNumbersConfirmed, bulkRunning, isAuthenticated, resolveBulkContractKey, selectedItems]);

  const activeManualUploadBatch = isActiveManualUploadBatch(manualUploadBatch) ? manualUploadBatch : null;
  const manualUploadPending = Number(activeManualUploadBatch?.pending || 0);
  const manualUploadRunningCount = Number(activeManualUploadBatch?.running || 0);

  const aufDeals = queueTab === 'deals';
  const neuLaden = aufDeals ? loadPendingDeals : () => loadManualUploadQueue({ drain: true });
  const laedtGerade = aufDeals ? loading : manualUploadLoading;
  const uploadZahl = manualUploadRunningCount + manualUploadPending;

  return (
    <div style={{ flex: 1, minHeight: 0, background: T.grund, overflowY: 'auto' }}>
      <div style={{
        margin: '16px 20px', border: `1px solid ${T.randWeich}`, borderRadius: 12, background: T.flaeche,
        display: 'flex', flexDirection: 'column', boxShadow: '0 1px 2px rgba(18, 42, 92, 0.04)',
      }}>
        {/* Kopf: Titel, Stand, Aktualisieren. Die Liste der ueberwachten
            Bitrix-Phasen stand bis zum 23.09.2026 als Untertitel hier - sie
            steht als Filter ohnehin direkt darunter. */}
        <div style={{ padding: '14px 16px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <ClipboardList size={17} color={T.navy} />
          <div style={{ flex: 1, fontSize: T.tPanel, fontWeight: 700, color: T.navy, letterSpacing: T.spurTitel }}>
            Verträge zu erstellen
          </div>
          {aufDeals && fetchedAt ? (
            <span style={{ fontSize: T.tMikro, color: T.muted }}>
              Stand {new Date(fetchedAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          ) : null}
          <button
            type="button"
            onClick={neuLaden}
            disabled={laedtGerade || !isAuthenticated}
            title={isAuthenticated ? 'Neu laden' : 'Bitte erst einloggen'}
            aria-label="Neu laden"
            style={{ ...knopfStil(), padding: '6px 8px', opacity: laedtGerade || !isAuthenticated ? 0.55 : 1 }}
          >
            {laedtGerade ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 18, padding: '0 16px', borderBottom: `1px solid ${T.randWeich}`, marginTop: 6 }}>
          <button type="button" onClick={() => setQueueTab('deals')}
            style={{ ...reiterStil(aufDeals), flex: '0 0 auto', padding: '10px 0' }}>
            Deals{items.length ? ` (${items.length})` : ''}
          </button>
          <button
            type="button"
            onClick={() => {
              setQueueTab('uploads');
              loadManualUploadQueue({ drain: true });
            }}
            style={{ ...reiterStil(!aufDeals), flex: '0 0 auto', padding: '10px 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Upload-Aufträge
            {uploadZahl > 0 ? (
              <span style={{
                minWidth: 17, height: 17, borderRadius: 999, background: T.orange, color: '#FFFFFF',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: T.tLabel, padding: '0 4px',
              }}>{uploadZahl}</span>
            ) : null}
          </button>
        </div>

        {!aufDeals ? (
          <UploadAuftraege batch={activeManualUploadBatch} historie={manualUploadHistory} fehler={manualUploadError} hinweis={manualUploadHinweis} />
        ) : null}

        <div style={{ display: aufDeals ? 'contents' : 'none' }}>
          <div style={{ padding: '14px 16px 12px', display: 'grid', gap: 10 }}>
            <PhasenFilter optionen={phaseFilterOptions} aktiv={activePhaseId} onWahl={setActivePhaseId} />
            <FilterZeile
              suche={queueSearchQuery} onSuche={setQueueSearchQuery}
              verantwortlich={responsibleFilter} onVerantwortlich={setResponsibleFilter} verantwortlichOptionen={responsibleOptions}
              art={typeFilter} onArt={setTypeFilter} artOptionen={typeOptions}
              sortierung={sortOrder} onSortierung={setSortOrder}
            />
          </div>

          <AuswahlLeiste
            anzahl={selectedItems.length}
            laeuft={bulkRunning}
            zusammenfassung={bulkSummary}
            angemeldet={isAuthenticated}
            vertrag={bulkContractKey}
            vertraege={CONTRACTS}
            autoSchluessel={BULK_CONTRACT_AUTO_KEY}
            onVertrag={(schluessel) => {
              setBulkContractKey(schluessel);
              setSelectedDealIds([]);
            }}
            pachtAnhaengen={bulkAppendLeaseContract}
            onPachtAnhaengen={(an) => {
              setBulkAppendLeaseContract(an);
              setBulkLeaseNumbersConfirmed(false);
              if (an) setBulkLeaseNumbersModalOpen(true);
            }}
            pachtVertrag={bulkLeaseContractKey}
            onPachtVertrag={(schluessel) => {
              setBulkLeaseContractKey(schluessel);
              setBulkLeaseNumbersConfirmed(false);
              if (bulkAppendLeaseContract) setBulkLeaseNumbersModalOpen(true);
            }}
            datenblatt={bulkAppendDatasheet}
            onDatenblatt={setBulkAppendDatasheet}
            download={bulkDownloadMode}
            onDownload={setBulkDownloadMode}
            onErstellen={() => runBulkGeneration()}
            onAuswahlAufheben={() => setSelectedDealIds([])}
          />

          {bulkLeaseNumbersModalOpen && bulkAppendLeaseContract ? (
            <PachtzahlenDialog
              anforderungen={bulkLeaseNumberRequirements}
              werte={bulkLeaseNumbers}
              onWert={updateBulkLeaseNumber}
              laeuft={bulkRunning}
              anzahl={selectedItems.length}
              onAbbrechen={() => setBulkLeaseNumbersModalOpen(false)}
              onStarten={() => {
                const missingLeaseNumbers = getMissingLeaseNumberLabels(bulkLeaseNumbers, bulkLeaseNumberRequirements);
                if (missingLeaseNumbers.length > 0) {
                  toast.error(`Bitte zuerst angeben: ${missingLeaseNumbers.join(', ')}`);
                  return;
                }
                setBulkLeaseNumbersConfirmed(true);
                setBulkLeaseNumbersModalOpen(false);
                runBulkGeneration({ bypassLeaseNumberPrompt: true });
              }}
            />
          ) : null}

          <div style={{ padding: 16, minHeight: 120 }}>
            {error ? (
              <div style={{
                padding: '9px 12px', borderRadius: T.rChip, background: T.gefahrGrund,
                borderLeft: `3px solid ${T.gefahr}`, color: T.gefahr, fontSize: T.tMeta, fontWeight: 600,
              }}>{error}</div>
            ) : loading && items.length === 0 ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: T.muted, fontSize: T.tText }}>
                <Loader2 size={14} className="animate-spin" /> Deals werden aus Bitrix geladen …
              </div>
            ) : filteredItems.length === 0 ? (
              <div style={{ color: T.muted, fontSize: T.tText }}>
                {items.length === 0 ? 'Gerade wartet kein Deal auf einen Vertrag.' : 'Kein Deal passt zu Suche und Filtern.'}
              </div>
            ) : (
              <DealsTabelle
                items={filteredItems}
                ausgewaehlt={selectedDealIdSet}
                statusJeDeal={bulkStatusByDealId}
                vertragFuer={resolveBulkContractKey}
                vertragsName={(schluessel) => CONTRACTS.find((contract) => contract.key === schluessel)?.label || 'Zugeordnet'}
                laeuft={bulkRunning}
                alleAusgewaehlt={allBulkCapableVisibleSelected}
                keinerWaehlbar={bulkCapableFilteredItems.length === 0}
                onAlle={toggleVisibleSelection}
                onEiner={toggleDealSelection}
                onOeffnen={(item) => onSelectDeal && onSelectDeal(item)}
                onHerunterladen={(datei) => downloadGeneratedFile(
                  datei.url,
                  datei.filename || `Vertrag.${datei.format === 'pdf' ? 'pdf' : 'html'}`,
                  authHeaders,
                )}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
